import { html } from "lit-html"
import { repeat } from "lit-html/directives/repeat"

import { virtual, useState } from "haunted"
import ActionMenu from "../generic/action-menu"
import TooltipIcons from "../generic/tooltip-icons"
import DocumentLinkList from "./doc-links"
import AssignmentAppButtonForm from "../forms/assignment-app"
import {
  ROLE_ICON_MAP,
  EDIT_PATH,
  CLONE_PATH,
  ROLE_ASSIGN_PATH,
} from "../constants"
import { keyClickWrapper } from "../utils"
import markdown from "../utils/markdown"

import DateTime from "./date-time"
import TagList from "./tag-list"
import CalLinks from "./cal-links"
import Location from "./location"

const getRoleIcons = (categories) =>
  Object.entries(categories)
    .sort(([categoryA], [categoryB]) => categoryA.localeCompare(categoryB))
    .map(([category, label]) => ({
      label,
      icon: ROLE_ICON_MAP[category] || "fas fa-pen-alt",
    }))

const ManagerColumn = ({ url, roles, applications, categories }) => {
  return html`
    <div class="flex-grow min-w-300 p-5 content bg-white">
      <p class="font-bold text-black">Applications</p>
      <p>
        <span class="tooltip-icon"><i class="fas fa-user"></i></span>
        <span>${applications.length}</span> applications
      </p>
      ${repeat(
        Object.entries(categories),
        ([category]) => category,
        ([category, label]) => {
          const categoryApps = applications.filter(({ categories: cArr }) =>
            cArr.includes(category)
          )
          const categoryRoles = roles.filter(
            ({ category: c }) => c === category
          )
          return html`
            <p>
              <span class="tooltip-icon">
                <i class="${ROLE_ICON_MAP[category] || "fas fa-file"}"></i>
                <span class="tooltip-icon-tooltip">${label}</span>
              </span>
              <span>
                ${categoryApps.length} for ${categoryRoles.length}
                ${categoryRoles.length === 1 ? `role` : `roles`}
              </span>
            </p>
          `
        }
      )}
      <div class="flex flex-row items-center justify-between mb-4">
        <span class="font-bold text-black mb-0">Roles</span>
        <a href="${url}${ROLE_ASSIGN_PATH}">Assign roles</a>
      </div>
      <div class="${roles.length > 2 ? `max-h-400 overflow-y-scroll` : ``}">
        ${repeat(
          roles,
          ({ id }) => id,
          ({ url: roleUrl, category, user }) =>
            html`<div class="flex flex-row items-center justify-start mt-4">
              <span class="icon-box"
                ><i class="${ROLE_ICON_MAP[category] || "fas fa-file"}"></i
              ></span>
              <div class="leading-tight">
                <span class="block">
                  <a href=${roleUrl}>${categories[category]}</a>
                </span>
                <span class="block">
                  ${user && user.id
                    ? user.full_name || user.name
                    : html`<span class="italic">Unassigned</span>`}
                </span>
              </div>
            </div>`
        )}
      </div>
    </div>
  `
}

const IconDetailBlock = ({ can_edit, applications, roles, categories }) => {
  if (can_edit) {
    return html`
      <div class="flex flex-row h-11">
        ${TooltipIcons(getRoleIcons(categories))}
        <div class="flex flex-col text-gray-icon">
          <div class="flex flex-row items-center justify-start flex-nowrap">
            <span class="w-7 h-7 flex flex-row items-center justify-center">
              <i class="fas fa-user"></i>
            </span>
            <span class="whitespace-nowrap text-gray-dark"
              >${applications.length}</span
            >
          </div>
          <div class="flex flex-row items-center justify-start flex-nowrap">
            <span class="w-7 h-7 flex flex-row items-center justify-center">
              <i class="fas fa-clipboard-list"></i>
            </span>
            <span class="whitespace-nowrap text-gray-dark"
              >${roles.length}</span
            >
          </div>
        </div>
      </div>
    `
  }
  return ``
}

const AssignmentRow = virtual(
  ({
    id,
    name,
    url,
    start_date,
    start_time,
    shortened_timezone,
    tags,
    remote,
    link,
    location,
    gcal_link,
    agency,
    documents,
    assignment_documents,
    roles,
    applications,
    can_edit,
    can_clone,
    app_form,
    status,
  }) => {
    const [expanded, setExpanded] = useState(false)

    const categories = roles.reduce(
      (accumulator, { category, label }) => ({
        ...accumulator,
        [category]: label,
      }),
      {}
    )

    return html`
      <div
        class="assignment filter-result-row group"
        role="button"
        tabindex="0"
        @click=${(e) => {
          if (e && e.target.tagName.toLowerCase() === "a") {
            return
          }
          setExpanded(!expanded)
        }}
        @keydown=${(e) => keyClickWrapper(e, () => setExpanded(!expanded))}
        aria-controls=${`item-${id}`}
        aria-expanded=${expanded.toString()}
      >
        <div class="w-5/6 lg:w-1/3 p-3 order-1">
          <p>
            <span
              class="text-blue group-hover:text-black group-focus:text-gray-dark group-focus:font-bold"
              >${name}</span
            >
          </p>
          <p>${agency ? agency.name : ""}</p>
        </div>
        <div class="w-2/3 lg:w-1/4 p-3 order-3 lg:order-2">
          ${DateTime({
            start_date,
            start_time,
            shortened_timezone,
            canceled: status.toLowerCase().includes("cancel"),
          })}
        </div>
        <div
          class="w-1/3 lg:w-1/6 p-3 order-4 lg:order-3 text-right lg:text-left"
        >
          ${TagList(tags)}
        </div>
        <div
          class="w-1/6 lg:w-1/4 p-3 order-2 lg:order-4 flex flex-row items-center justify-end text-right"
        >
          <div class="hidden lg:flex py-1 flex-row items-center">
            ${status === "Open"
              ? IconDetailBlock({
                  can_edit,
                  status,
                  applications,
                  roles,
                  categories,
                })
              : ``}
            <div
              class="status-block ml-1 assignment-status ${status.toLowerCase()} touch:hidden mr-2"
            >
              ${status}
            </div>
          </div>
          ${ActionMenu({
            children: html`
              ${can_edit
                ? html`
                    <a
                      class="dropdown-item"
                      role="menuitem"
                      href=${`${url}${EDIT_PATH}`}
                    >
                      Edit assignment
                    </a>
                  `
                : ""}
              ${can_clone
                ? html`
                    <a
                      class="dropdown-item"
                      role="menuitem"
                      href=${`${url}${CLONE_PATH}`}
                    >
                      Duplicate assignment
                    </a>
                  `
                : ""}
              <a class="dropdown-item" role="menuitem" href=${url}>
                View assignment details
              </a>
              ${CalLinks({ url, gcal_link })}
            `,
          })}
        </div>
        <div
          class="flex py-1 flex-row items-center w-full lg:hidden p-3 order-5 assignment-detail-block"
        >
          <div
            class="status-block assignment-status mr-1 ${status.toLowerCase()}"
          >
            ${status}
          </div>
          ${status === "Open"
            ? IconDetailBlock({
                can_edit,
                status,
                applications,
                roles,
                categories,
              })
            : ``}
        </div>
        <hr class="filter-result-row-divider" />
      </div>
      <div
        class="collapse-row py-1 flex flex-row flex-wrap"
        id=${`item-${id}`}
        aria-hidden=${(!expanded).toString()}
      >
        ${expanded
          ? html`
              <div class="p-5 max-w-570 flex-grow">
                <div class="content mb-6">
                  ${Location({ location, link, remote })}
                  ${agency && agency.description
                    ? markdown(agency.description)
                    : html`
                        <p>
                          ${agency
                            ? "Agency description not available"
                            : "This assignment is not for a meeting hosted by a public agency"}
                        </p>
                      `}
                </div>
                ${DocumentLinkList(documents || assignment_documents, url)}
                ${app_form
                  ? AssignmentAppButtonForm({
                      ...app_form,
                      className: "mb-4 button-submit",
                    })
                  : ``}
                <a
                  href=${url}
                  data-link="details"
                  class="flex items-center justify-center h-10 w-full bg-blue text-white rounded hover:bg-blue-dark focus:bg-blue-dark"
                >
                  View full assignment details
                </a>
              </div>
              ${can_edit
                ? ManagerColumn({
                    id,
                    url,
                    status,
                    roles,
                    categories,
                    applications,
                  })
                : ""}
            `
          : ""}
      </div>
    `
  }
)

export default AssignmentRow
